import { Link as RouterLink } from 'react-router-dom';
import styles from './styles.module.scss';

type BaseProps = {
  /** リンクの子要素 */
  children: React.ReactNode;
  /** リンク先のURL（内部リンクの場合はパス、外部リンクの場合は完全なURL） */
  to: string;
  /** 追加のCSSクラス名 */
  className?: string;
  /**
   * クリック時のイベントハンドラ
   *
   * stopPropagation などのイベント制御用途で使用する。
   * 内部リンク（RouterLink）・外部リンク（a タグ）の両方に適用される。
   */
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
};

type InternalLinkProps = BaseProps & {
  /** 外部リンクかどうか。trueの場合は通常のaタグ、falseの場合はReact RouterのLinkコンポーネントを使用 */
  isExternal?: false;
  /** リンクの開き方（_blank, _self, _parent, _topなど） */
  target?: never;
  /** リンクのrel属性。target="_blank"の場合は自動的にnoopenerとnoreferrerが追加されます */
  rel?: never;
};

type ExternalLinkProps = BaseProps & {
  /** 外部リンクかどうか。trueの場合は通常のaタグ、falseの場合はReact RouterのLinkコンポーネントを使用 */
  isExternal: true;
  /** リンクの開き方（_blank, _self, _parent, _topなど） */
  target?: React.AnchorHTMLAttributes<HTMLAnchorElement>['target'];
  /** リンクのrel属性。target="_blank"の場合は自動的にnoopenerとnoreferrerが追加されます */
  rel?: string;
};

type Props = InternalLinkProps | ExternalLinkProps;

/**
 * リンクコンポーネント
 *
 * 内部リンクと外部リンクの両方をサポートする統一的なリンクコンポーネントです。
 * 内部リンクの場合はReact RouterのLinkコンポーネントを使用し、
 * 外部リンクの場合は通常のaタグを使用します。
 *
 * target="_blank"が指定された場合、セキュリティのため自動的に
 * rel属性にnoopenerとnoreferrerが追加されます。
 *
 * @example
 * ```tsx
 * // 内部リンク
 * <Link to="/dashboard">ダッシュボード</Link>
 *
 * // 外部リンク
 * <Link to="https://example.com" isExternal>外部サイト</Link>
 *
 * // 新しいタブで開く外部リンク
 * <Link to="https://example.com" isExternal target="_blank">
 *   外部サイト（新しいタブ）
 * </Link>
 * ```
 */
export const Link: React.FC<Props> = ({
  children,
  to,
  className,
  isExternal = false,
  target,
  rel,
  onClick,
}) => {
  if (isExternal) {
    const safeRel =
      target === '_blank'
        ? Array.from(
            new Set([...(rel?.split(/\s+/).filter(Boolean) || []), 'noopener', 'noreferrer'])
          ).join(' ')
        : rel;
    return (
      <a
        href={to}
        className={[styles.link, className].filter(Boolean).join(' ')}
        target={target}
        rel={safeRel}
        onClick={onClick}
      >
        {children}
      </a>
    );
  }

  return (
    <RouterLink
      to={to}
      className={[styles.link, className].filter(Boolean).join(' ')}
      onClick={onClick}
    >
      {children}
    </RouterLink>
  );
};
